// Single source of truth for per-route head metadata (title, description,
// canonical, og:*). Consumed by <RouteMeta /> and by the sitemap generator.

export const SITE_NAME = "Home Hunter Pro";
export const SITE_URL = "https://homehunterpro.com";

export interface RouteMeta {
  /** Route path as registered in App.tsx */
  path: string;
  title: string;
  description: string;
  /** Whether the route belongs in the sitemap (public, indexable) */
  indexable: boolean;
}

export const ROUTE_META: RouteMeta[] = [
  {
    path: "/",
    title: `${SITE_NAME} — Your Path to Homeownership`,
    description:
      "Plan your home purchase with mortgage calculators, market analysis, government programs, and a step-by-step homebuyer journey.",
    indexable: true,
  },
  {
    path: "/calculators",
    title: `Mortgage & Affordability Calculators — ${SITE_NAME}`,
    description:
      "Nine free calculators for mortgage payments, affordability, DTI, PMI, closing costs, property tax, and refinancing.",
    indexable: true,
  },
  {
    path: "/market",
    title: `Housing Market Analysis by Town — ${SITE_NAME}`,
    description:
      "Compare median prices, appreciation forecasts, days on market, schools, and inventory across towns before you buy.",
    indexable: true,
  },
  {
    path: "/brrrr",
    title: `BRRRR Method Planner for Rental Investors — ${SITE_NAME}`,
    description:
      "Model Buy, Rehab, Rent, Refinance, Repeat deals: ARV, refinance loan amount, cash left in deal, and cash-on-cash return.",
    indexable: true,
  },
  {
    path: "/renovations",
    title: `Home Renovation ROI Calculator — ${SITE_NAME}`,
    description:
      "See which remodels pay back at resale, with cost ranges and recouped-value estimates for kitchens, baths, roofs, and more.",
    indexable: true,
  },
  {
    path: "/programs",
    title: `First-Time Buyer Programs & Tax Credits — ${SITE_NAME}`,
    description:
      "Search government loans, grants, down payment assistance, and tax credits that help you afford a home sooner.",
    indexable: true,
  },
  {
    path: "/journey",
    title: `Step-by-Step Homebuyer Journey — ${SITE_NAME}`,
    description:
      "Follow the eight stages of buying a home: save, credit, qualify, search, offer, inspect, negotiate, and close.",
    indexable: true,
  },
  {
    path: "/search",
    title: `Property Search — ${SITE_NAME}`,
    description:
      "Search listings and compare homes with price history, estimated costs, and neighborhood market data.",
    indexable: true,
  },
  {
    path: "/advisor",
    title: `Should I Buy Now? AI Advisor — ${SITE_NAME}`,
    description:
      "Get a personalized buy-or-wait recommendation based on your finances, documents, and current market conditions.",
    indexable: true,
  },
  {
    path: "/auth",
    title: `Sign In — ${SITE_NAME}`,
    description: "Sign in or create a free Home Hunter Pro account to save your progress, properties, and documents.",
    indexable: true,
  },
  {
    path: "/property/:id",
    title: `Property Details — ${SITE_NAME}`,
    description: "Property details with estimated monthly costs, renovation potential, and local market context.",
    indexable: false,
  },
  {
    path: "/documents",
    title: `Document Vault — ${SITE_NAME}`,
    description: "Securely store and analyze your mortgage documents with AI-powered insights.",
    indexable: false,
  },
  {
    path: "/achievements",
    title: `Your Achievements — ${SITE_NAME}`,
    description: "Track milestones on your path to homeownership.",
    indexable: false,
  },
  {
    path: "/profile",
    title: `Profile Settings — ${SITE_NAME}`,
    description: "Manage your Home Hunter Pro profile, avatar, and notification preferences.",
    indexable: false,
  },
  {
    path: "/reset-password",
    title: `Reset Password — ${SITE_NAME}`,
    description: "Reset the password for your Home Hunter Pro account.",
    indexable: false,
  },
];

export const FALLBACK_META: RouteMeta = {
  path: "*",
  title: `Page Not Found — ${SITE_NAME}`,
  description: "The page you are looking for could not be found.",
  indexable: false,
};

/** Resolve metadata for a concrete pathname (handles dynamic segments). */
export function resolveRouteMeta(pathname: string): RouteMeta {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, "") : "/";
  const exact = ROUTE_META.find((r) => r.path === clean);
  if (exact) return exact;
  if (/^\/property\/[^/]+$/.test(clean)) {
    return ROUTE_META.find((r) => r.path === "/property/:id")!;
  }
  return FALLBACK_META;
}

/** Canonical/og:url for a concrete pathname. */
export function canonicalUrl(pathname: string): string {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, "") : "/";
  return `${SITE_URL}${clean === "/" ? "/" : clean}`;
}
